import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { MediaCleanupInterface } from './MediaCleanupInterface.tsx';
import { AuditLogViewer } from './AuditLogViewer.tsx';
import { BulkMediaOperations } from './BulkMediaOperations.tsx';
import { CloudinaryBrowser } from './CloudinaryBrowser.tsx';

interface EntityTypeBreakdown {
  entityType: string;
  count: number;
  totalSize: number;
}

interface AdminMediaDashboardProps {
  className?: string;
}

type TabType = 'overview' | 'cleanup' | 'audit' | 'bulk' | 'cloudinary';

export function AdminMediaDashboard({ className = '' }: AdminMediaDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  // Get system-wide media statistics
  const { data: deletedMedia, isLoading: deletedLoading } = trpc.media.listDeletedMedia.useQuery({
    limit: 10,
    offset: 0,
  });

  const { data: orphanReport, isLoading: orphanLoading } = trpc.media.getOrphanReport.useQuery();

  const tabs = [
    { id: 'overview' as const, label: 'Overview', count: deletedMedia?.total },
    { id: 'cleanup' as const, label: 'Cleanup', count: orphanReport?.summary.totalOrphans },
    { id: 'audit' as const, label: 'Audit Logs' },
    { id: 'bulk' as const, label: 'Bulk Operations' },
    { id: 'cloudinary' as const, label: 'Cloudinary Browser' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* System Statistics */}
              <div className="bg-gray-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">System Overview</h3>
                {deletedLoading ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deleted Media:</span>
                      <span className="font-medium">{deletedMedia?.total || 0}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Orphan Statistics */}
              <div className="bg-gray-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Orphan Detection</h3>
                {orphanLoading ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Orphaned Files:</span>
                      <span className="font-medium text-red-600">
                        {orphanReport?.summary.totalOrphans || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Size:</span>
                      <span className="font-medium">
                        {formatBytes(orphanReport?.summary.totalSize ?? 0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-gray-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setActiveTab('cleanup')}
                    className="w-full text-left px-3 py-2 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors"
                  >
                    Run Cleanup
                  </button>
                  <button
                    onClick={() => setActiveTab('audit')}
                    className="w-full text-left px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors"
                  >
                    View Audit Logs
                  </button>
                  <button
                    onClick={() => setActiveTab('bulk')}
                    className="w-full text-left px-3 py-2 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors"
                  >
                    Bulk Operations
                  </button>
                </div>
              </div>
            </div>

            {/* Entity Type Breakdown */}
            {orphanReport?.summary.byEntityType && (
              <div className="bg-gray-50 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Orphans by Entity Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orphanReport.summary.byEntityType.map((item: EntityTypeBreakdown) => (
                    <div key={item.entityType} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium capitalize">{item.entityType}</span>
                        <span className="text-sm text-gray-500">{item.count} files</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {formatBytes(item.totalSize)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'cleanup':
        return <MediaCleanupInterface />;

      case 'audit':
        return <AuditLogViewer />;

      case 'bulk':
        return <BulkMediaOperations />;

      case 'cloudinary':
        return <CloudinaryBrowser />;

      default:
        return null;
    }
  };

  return (
    <div className={`max-w-7xl mx-auto ${className}`}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Media Management Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Manage media across the system, clean up orphaned files, and view audit logs.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-700 text-primary-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}